/**
 * Thunk-side entry point for the shared async-action abstraction.
 *
 * Interface modules (e.g., `schemaManagementInterface`, `sypi-interface`,
 * `file-interface`) are plain modules — they cannot call the `useAsyncAction`
 * hook. This wrapper accepts a store-like handle and delegates to the shared
 * `runAsyncActionCore`.
 *
 *   await runAsyncAction(
 *     store,
 *     () => filesAdapter.patchFile(id, body),
 *     { errorAction: "update", errorSubject: "file",
 *       successToast: "File updated." }
 *   );
 *
 * Accepts either `{ dispatch, getState }` (AppStoreLike / thunk arg) or a
 * lone dispatch function.
 */

import {
  runAsyncActionCore,
  type AsyncActionOptions,
  type AsyncActionResult,
  type DispatchLike,
} from "./async-action-core";

interface StoreLike {
  dispatch: DispatchLike;
}

export function runAsyncAction<T>(
  storeOrDispatch: StoreLike | DispatchLike,
  action: () => Promise<T>,
  options?: AsyncActionOptions<T>
): Promise<AsyncActionResult<T>> {
  const dispatch =
    typeof storeOrDispatch === "function"
      ? storeOrDispatch
      : storeOrDispatch.dispatch;
  return runAsyncActionCore(dispatch, action, options);
}

export type { AsyncActionOptions, AsyncActionResult } from "./async-action-core";
